import {useEffect, useMemo, useState} from 'react';
import './user-caregiver-form.scss';
import {useHistory} from 'react-router-dom';
import moment from 'moment';
import {useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';
import {updateAppLanguage} from '../../i18n/functions';
import {Alert, Avatar, Box, Button, Stack} from '@mui/material';
import {Person} from '@mui/icons-material';
import {BsInfoCircle} from 'react-icons/bs';
import {useSnackBar} from '../../contexts/snack-bar/context';
import {useAuth} from '../../contexts/auth/context';
import {useOffice} from '../../contexts/office/context';
import {ContentPage} from '../content-page/content-page';
import {LoadingContent} from '../loading-content/loading-content';
import {SubmitButton, SubmitButtonState} from '../submit-button/submit-button';
import {PhoneNumberInput} from '../phone-number-input/phone-number-input';
import {EditPicture} from '../edit-picture/edit-picture';
import {OccupationInput, OccupationOption} from '../occupation-input/occupation-input';
import FlatInput from '../flat-input/flat-input';
import {FlatSelectDropdown} from '../flat-select-dropdown/flat-select-dropdown';
import {RouterPrompt} from '../router-prompt/router-prompt';
import {FlatMultiTextSelect} from '../flat-multi-text-select/flat-multi-text-select';
import {User} from '../../backend/interfaces/user';
import {Occupation} from '../../backend/interfaces/occupation';
import {UserGender, RegionCode, BackendLanguageTag} from '../../backend/enums';
import {PutUser} from '../../backend/requests/put-user';
import {UserFormType} from '../../enums/user-form-type';
import {UsersRepository} from '../../repositories/users';
import {OccupationsRepository} from '../../repositories/occupations';
import {EntitiesRepository} from '../../repositories/entities';
import {parsePhoneNumber, PhoneNumber} from 'libphonenumber-js/max';
import {
  FULL_NAME_MAX_CHARACTERS,
  DISPLAY_NAME_MAX_CHARACTERS,
  avatarSizesAndCoefficients,
  appSizes,
  QUICK_ACCESS_CODE_MAX_CHARACTERS,
  PROCESS_NUMBER_MAX_CHARACTERS,
} from '../../utils/constants';
import {languages, genders, regions} from '../../utils/data';
import {
  getBackendLanguageFromAppLanguage,
  getBackendLanguageFromBrowserLanguage,
} from '../../utils/functions';
import {RolesRepository} from '../../repositories/roles';
import {useFetchAllItems} from '../../hooks/fetch-all-items';
import {UserType} from '../../backend/enums/user-type';
import {AffiliationQuickAccess} from '../../backend/interfaces/affiliation-quick-access';
import {
  emailValidation,
  nameValidation,
  taxPayerNumberValidation,
  phoneNumberValidation,
  quickAccessValidation,
} from './user-form-validations';
import {processProfilePicture} from './user-form-utils';
import {FlatSelectDate} from '../flat-select-date/flat-select-date';

type PhoneNumberExtension = {
  phone: string;
  code: string;
};

interface UserFormProps {
  userFormType:
    | UserFormType.EditCurrentUser
    | UserFormType.EditCaregiver_SystemAdministrator
    | UserFormType.CreateEditRemoveSenior_ProfessionalCaregiver
    | UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverAdministrator
    | UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator;
  redirectUrl: string;
  entityId?: string;
  canEditRole?: boolean;
}

/**
 * Form to view, create, edit, and remove users (seniors, caregivers, current user):
 * - Read: all user types
 * - Create: seniors
 * - Edit: seniors, caregivers, and current user
 * - Remove: seniors
 *
 * It uses the office context to get the user's information.
 *
 * Note: In the future, maybe we will need to add more user form types to set read and write
 * permissions for the senior's profile because it may depend on the current user permissions.
 */
export const UserCaregiverForm = (props: UserFormProps) => {
  let history = useHistory();
  const {t} = useTranslation();
  const defaultPhoneNumberExtension: PhoneNumberExtension = {
    phone: '+351',
    code: 'PT',
  };
  const officeContext = useOffice();
  const snackBarContext = useSnackBar();
  const {
    state: authState,
    refreshSessionData,
    subscribeFirebasePushNotifications,
    updateSessionCurrentUser,
  } = useAuth();
  const [model, setModel] = useState<User | undefined>();
  const [originalModel, setOriginalModel] = useState<User | undefined>();
  const [countryCode, setCountryCode] = useState<PhoneNumberExtension>(defaultPhoneNumberExtension);
  const [occupation, setOccupation] = useState<OccupationOption | undefined>();
  const [profilePictureFile, setProfilePictureFile] = useState<File | undefined>();
  const [originalRolesIds, setOriginalRolesIds] = useState<string[] | undefined>(undefined);
  const [rolesIds, setRolesIds] = useState<string[] | undefined>(undefined);
  const [originalQuickAccessCode, setOriginalQuickAccessCode] = useState<string | undefined>();
  const [quickAccessCode, setQuickAccessCode] = useState<string | undefined>();
  const [showQuickAccessCodeError, setShowQuickAccessCodeError] = useState<boolean>(false);
  const [processNumber, setProcessNumber] = useState<string | undefined>();
  const [originalProcessNumber, setOriginalProcessNumber] = useState<string | undefined>();
  const [showProcessNumberError, setShowProcessNumberError] = useState<boolean>(false);
  const [processNumberError, setProcessNumberError] = useState<string | undefined>();

  // Form validation
  const [dataChanged, setDataChanged] = useState<boolean>(false);
  const [fullNameError, setFullNameError] = useState<string | undefined>();
  const [quickAccessCodeError, setQuickAccessCodeError] = useState<string | undefined>();
  const [showFullNameError, setShowFullNameError] = useState<boolean>(false);
  const [displayNameError, setDisplayNameError] = useState<string | undefined>();
  const [showDisplayNameError, setShowDisplayNameError] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<string | undefined>();
  const [showEmailError, setShowEmailError] = useState<boolean>(false);
  const [taxPayerNumberError, setTaxPayerNumberError] = useState<string | undefined>();
  const [showTaxPayerNumberError, setShowTaxPayerNumberError] = useState<boolean>(false);
  const [phoneNumberError, setPhoneNumberError] = useState<string | undefined>();
  const [showPhoneNumberError, setShowPhoneNumberError] = useState<boolean>(false);
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState<boolean>(true);

  // Set user
  useEffect(() => {
    const init = async () => {
      if (officeContext.state.userForm) {
        // Init buttons state
        officeContext.setUserFormDeleteButtonState(SubmitButtonState.Normal);
        officeContext.setUserFormSubmitButtonState(SubmitButtonState.Normal);

        // Init some state
        setProfilePictureFile(undefined);
        setDataChanged(false);

        // Set models for edit/view user
        if (officeContext.state.userForm.user.id) {
          // Get occupation detail from backend
          if (officeContext.state.userForm.user.occupationId) {
            try {
              // Fetch occupation details
              const occupationDetails: Array<Occupation> =
                await OccupationsRepository.instance.getById(
                  officeContext.state.userForm.user.occupationId,
                  true
                );

              // Find occupation
              const userOccupation: Occupation | undefined =
                occupationDetails && Array.isArray(occupationDetails)
                  ? occupationDetails.find(
                      (obj) => obj.id === officeContext.state.userForm?.user.occupationId
                    )
                  : undefined;

              if (userOccupation && userOccupation.id) {
                // Set state
                setOccupation({
                  occupationId: userOccupation.id,
                  occupationDesignation: userOccupation.designation,
                });
              } else {
                // Error
                throw new Error('Empty occupation.');
              }
            } catch (error) {
              // Log
              console.debug('🔴 Could not get occupation details.', error);
            }
          }

          if (officeContext.state.userForm.affiliation) {
            // Set roles ids
            setRolesIds(officeContext.state.userForm.affiliation.rolesIds);
            setOriginalRolesIds(officeContext.state.userForm.affiliation.rolesIds);

            setOriginalQuickAccessCode(officeContext.state.userForm.affiliationQuickAccess?.code);
            setQuickAccessCode(officeContext.state.userForm.affiliationQuickAccess?.code);
            setOriginalProcessNumber(officeContext.state.userForm.affiliation.processNumber);
            setProcessNumber(officeContext.state.userForm.affiliation.processNumber);
          }

          // Note: The original model will have the complete phone number (ex: "+351910000000").
          // The model will only have the phone number without the country calling code.

          // Parse phone number
          let parsedPhoneNumber: PhoneNumber | undefined;
          if (officeContext.state.userForm.user.phoneNumber) {
            try {
              parsedPhoneNumber = parsePhoneNumber(officeContext.state.userForm.user.phoneNumber);
            } catch (error) {
              // Log
              console.debug('🔴 Could not parse phone number in UserForm.', error);
            }
          }

          // Model
          const userModel: User = {
            ...officeContext.state.userForm.user,
            phoneNumber:
              parsedPhoneNumber && parsedPhoneNumber.isValid()
                ? parsedPhoneNumber.nationalNumber.toString()
                : undefined,
          };

          // Set models
          setOriginalModel(officeContext.state.userForm.user);
          setModel(userModel);

          // Country code and international phone number
          if (parsedPhoneNumber && parsedPhoneNumber.isValid()) {
            // Set country code
            setCountryCode({
              phone: `+${parsedPhoneNumber.countryCallingCode.toString()}`,
              code: parsedPhoneNumber.country?.toString() || '',
            });
          }
        } else {
          // Set models for create user
          setOriginalModel(officeContext.state.userForm.user);
          setModel(officeContext.state.userForm.user);
        }
        officeContext.setUserFormLoadState(false);

        // Log
        console.debug('🆕 User set in UserCaregiverForm component.');
      }
    };

    // Init
    init();

    // Clean up
    return () => {
      // Log
      console.debug('🧹 UserCaregiverForm button states cleaned in office context.');

      // Clear button states
      officeContext.clearUserFormDeleteButtonState();
      officeContext.clearUserFormSubmitButtonState();
      officeContext.setUserFormLoadState(true);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officeContext.state.userForm]);

  const {items: roles} = useFetchAllItems({
    fetchFunction: ({limit, skip}) => {
      return RolesRepository.instance.getRoles(props.entityId!, limit, skip);
    },
    enabled: !officeContext.state.userForm?.user
      ? false
      : // Only validate if userForm is not empty.
        officeContext.state.userForm.user.type !== UserType.Senior && props.entityId !== undefined,
  });

  const rolesOptions = useMemo(() => {
    if (!roles) {
      return [];
    }

    return roles.map((r) => ({key: r.id!, label: r.name}));
  }, [roles]);

  const canEditUserData =
    props.userFormType === UserFormType.EditCurrentUser ||
    props.userFormType === UserFormType.EditCaregiver_SystemAdministrator ||
    props.userFormType === UserFormType.CreateEditRemoveSenior_ProfessionalCaregiver;
  const canEditAffiliationData = !!props.canEditRole && !!props.entityId;
  const canShowQuickAccessCode =
    props.userFormType === UserFormType.EditCurrentUser || canEditAffiliationData;

  // Form validation
  useEffect(() => {
    if (model && originalModel) {
      let dataWasUpdated: boolean = false;

      if (canEditUserData) {
        // ----- Fields validation -----
        // Hold validation
        let fullNameValid: boolean = false;
        let phoneNumberValid: boolean = false;
        let emailValid: boolean = true;
        let taxPayerNumberValid: boolean = false;
        let displayNameValid: boolean = false;

        // Full name validation
        const fullNameValidation = nameValidation(FULL_NAME_MAX_CHARACTERS, model.name);
        fullNameValid = fullNameValidation.valid;
        if (fullNameValidation.valid) {
          setFullNameError(undefined);
        } else if (fullNameValidation.error) {
          setFullNameError(t([fullNameValidation.error], {max: fullNameValidation.max}));
        }

        //Display name validation
        const displayNameValidation = nameValidation(
          DISPLAY_NAME_MAX_CHARACTERS,
          model.displayName
        );
        displayNameValid = displayNameValidation.valid;
        if (displayNameValidation.valid) {
          setDisplayNameError(undefined);
        } else if (displayNameValidation.error) {
          setDisplayNameError(t([displayNameValidation.error], {max: displayNameValidation.max}));
        }

        // Phone number validation
        const phoneValidation =
          !model.phoneNumber || model.phoneNumber.trim().length === 0
            ? {valid: true}
            : phoneNumberValidation(countryCode.phone, undefined, model.phoneNumber);
        phoneNumberValid = phoneValidation.valid;

        if (phoneValidation.valid) {
          setPhoneNumberError(undefined);
        } else if (!phoneValidation.valid && phoneValidation.error) {
          setPhoneNumberError(t([phoneValidation.error]));
        }

        if (
          props.userFormType === UserFormType.EditCaregiver_SystemAdministrator ||
          props.userFormType === UserFormType.EditCurrentUser
        ) {
          // Email validation
          const validationEmail = emailValidation(model.email);
          emailValid = validationEmail.valid;
          if (validationEmail.error) {
            setEmailError(t([validationEmail.error]));
          } else {
            setEmailError(undefined);
          }
        }

        // Tax payer number validation
        const taxPayerValidation = taxPayerNumberValidation(
          model.taxPayerNumber,
          model.region || authState.session?.selectedAffiliation?.entity.region
        );
        taxPayerNumberValid = taxPayerValidation.valid;
        if (taxPayerValidation.error) {
          setTaxPayerNumberError(
            t([taxPayerValidation.error], {
              digits: taxPayerValidation.digits,
              region: taxPayerValidation.region
                ? t(translations.regions[taxPayerValidation.region])
                : '',
            })
          );
        } else {
          setTaxPayerNumberError(undefined);
        }

        // ----- Fields validation -----

        // ----- Data changed -----
        // Validate phoneNumber
        let phoneNumberChanged = false;
        if (
          (!originalModel.phoneNumber && model.phoneNumber) ||
          (originalModel.phoneNumber && !model.phoneNumber) ||
          (model.phoneNumber &&
            originalModel.phoneNumber &&
            `${countryCode.phone}${model.phoneNumber}` !== originalModel.phoneNumber)
        ) {
          phoneNumberChanged = true;
        }

        // Validate birthday
        let birthdayChanged = false;
        if (
          (model.birthday && !originalModel.birthday) ||
          (!model.birthday && originalModel.birthday) ||
          (model.birthday &&
            originalModel.birthday &&
            !moment(model.birthday).isSame(originalModel.birthday, 'day'))
        ) {
          birthdayChanged = true;
        }

        if (
          (model.name || '') !== (originalModel.name || '') ||
          (model.displayName || '') !== (originalModel.displayName || '') ||
          (model.email || '') !== (originalModel.email || '') ||
          (model.taxPayerNumber || '') !== (originalModel.taxPayerNumber || '') ||
          (model.address || '') !== (originalModel.address || '') ||
          model.isActive !== originalModel.isActive ||
          model.picture !== originalModel.picture ||
          model.gender !== originalModel.gender ||
          model.languageCode !== originalModel.languageCode ||
          model.religion !== originalModel.religion ||
          model.region !== originalModel.region ||
          model.occupationId !== originalModel.occupationId ||
          birthdayChanged ||
          phoneNumberChanged
        ) {
          // Set it
          dataWasUpdated = true;

          // Set it
          setDataChanged(true);
        } else {
          // Set it
          dataWasUpdated = false;

          // Set it
          setDataChanged(false);
        }

        // ----- Data changed -----

        // ----- Submit button validation -----
        if (canEditUserData) {
          if (
            fullNameValid &&
            displayNameValid &&
            taxPayerNumberValid &&
            phoneNumberValid &&
            dataWasUpdated &&
            emailValid
          ) {
            // Enable submit button
            setSubmitButtonDisabled(false);
          } else {
            // Disable submit button
            setSubmitButtonDisabled(true);
          }
        } else {
          // Disable submit button
          setSubmitButtonDisabled(true);
        }
        // ----- Submit button validation -----
      }

      // Check if assigned roles changed
      if (props.canEditRole && props.entityId) {
        // Process Number validation
        let processNumberValid = true;
        if (processNumber && processNumber.length > PROCESS_NUMBER_MAX_CHARACTERS) {
          setProcessNumberError(
            t(translations.user_form.errors.form_validation_process_number_max_chars, {
              max: PROCESS_NUMBER_MAX_CHARACTERS,
            })
          );
          processNumberValid = false;
        } else {
          setProcessNumberError(undefined);
        }

        // Process Number changed
        let processNumberChanged = false;
        if (processNumber !== originalProcessNumber) {
          processNumberChanged = true;
        }

        // Quick Access Code
        let quickAccessCodeChanged = false;
        if (
          (quickAccessCode &&
            // Quick Access Code existed and will be removed
            originalQuickAccessCode &&
            quickAccessCode === '') ||
          // Quick Access Code existed and is now different
          (originalQuickAccessCode && quickAccessCode !== '****') ||
          // Quick Access Code did not existed but it will
          (!originalQuickAccessCode && quickAccessCode && quickAccessCode.length > 0)
        ) {
          // Quick Access Code changed
          quickAccessCodeChanged = true;
        } else {
          quickAccessCodeChanged = false;
        }

        // Validate Quick Access Code
        const quickAccessCodeValidation = quickAccessValidation(quickAccessCode);
        let rolesChanged = false;
        if (quickAccessCodeValidation.error) {
          setQuickAccessCodeError(
            t([quickAccessCodeValidation.error], {digits: quickAccessCodeValidation.max})
          );
        } else {
          setQuickAccessCodeError(undefined);
        }
        if (
          originalRolesIds?.length === rolesIds?.length &&
          originalRolesIds?.every((roleId) => rolesIds?.includes(roleId) && !dataWasUpdated)
        ) {
          rolesChanged = false;
        } else {
          rolesChanged = true;
        }

        if (
          (rolesChanged || quickAccessCodeChanged || processNumberChanged) &&
          quickAccessCodeValidation.valid &&
          processNumberValid
        ) {
          // ----- Data changed -----
          // ----- Submit button validation -----
          setDataChanged(true);
          setSubmitButtonDisabled(false);
          // ----- Data changed -----
          // ----- Submit button validation -----
        } else if (!dataWasUpdated) {
          setDataChanged(false);
          setSubmitButtonDisabled(true);
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    model,
    originalModel,
    countryCode,
    fullNameError,
    displayNameError,
    phoneNumberError,
    emailError,
    taxPayerNumberError,
    showFullNameError,
    showDisplayNameError,
    showPhoneNumberError,
    showEmailError,
    showTaxPayerNumberError,
    props.userFormType,
    originalRolesIds,
    rolesIds,
    quickAccessCodeError,
    showQuickAccessCodeError,
    originalQuickAccessCode,
    quickAccessCode,
    processNumber,
    originalProcessNumber,
    showProcessNumberError,
  ]);

  // Handle submit
  const handleSubmit = async () => {
    // Handle Data
    officeContext.setUserFormLoadState(true);
    const [newUserData, , affiliationQuickAccess] = await Promise.all([
      // user data
      canEditUserData ? handleSaveUser() : undefined,

      // user roles data
      props.canEditRole && props.entityId ? handleUpdateRolesIds() : undefined,

      // Update Quick Access Code
      handleUpdateQuickAccessCode(),
    ]);

    // Reset Context
    if (originalModel) {
      officeContext.setUserForm(
        newUserData ?? originalModel,
        officeContext.state.userForm?.affiliation
          ? {
              ...officeContext.state.userForm.affiliation,
              rolesIds,
              processNumber: processNumber || undefined,
            }
          : undefined,
        affiliationQuickAccess
      );
    }
  };

  // Handle submit
  const handleSaveUser = async (): Promise<User | undefined> => {
    // Stop execution if the model doesn't exist or the user don't have the right permissions
    // It's not expected to fall back here
    if (!model || !originalModel || !canEditUserData) {
      return;
    }

    try {
      // Update submit button state
      officeContext.setUserFormSubmitButtonState(SubmitButtonState.Loading);

      // Format phone number
      let phoneNumber: string | null = null;

      if (model.phoneNumber) {
        try {
          // Parse phone number
          const parsedPhoneNumber: PhoneNumber | undefined = parsePhoneNumber(
            `${countryCode.phone}${model.phoneNumber}`
          );

          // Check if the phone number is valid
          if (parsedPhoneNumber && parsedPhoneNumber.isValid()) {
            // Set it
            phoneNumber = parsedPhoneNumber.number.toString();
          } else {
            // Error
            throw new Error('Phone number parse failed.');
          }
        } catch (error) {
          // Error
          console.debug('Could not process phone number.', error);
        }
      }

      if (
        (props.userFormType === UserFormType.EditCaregiver_SystemAdministrator ||
          props.userFormType === UserFormType.CreateEditRemoveSenior_ProfessionalCaregiver) &&
        model.id
      ) {
        // -----> Edit senior or caregiver <-----

        // Profile picture to upload
        let processedProfilePicture: string | undefined;
        if (profilePictureFile) {
          processedProfilePicture = await processProfilePicture(model.id, profilePictureFile);
        } else {
          processedProfilePicture = model.picture;
        }

        // Request body
        const requestBody: PutUser = {
          product: model.product,
          type: model.type,
          isActive: model.isActive,
          picture: processedProfilePicture,
          name: model.name,
          displayName: model.displayName,
          gender: model.gender,
          birthday: model.birthday, // UTC
          phoneNumber: phoneNumber,
          email:
            props.userFormType === UserFormType.EditCaregiver_SystemAdministrator
              ? model.email
              : undefined,
          address: model.address,
          languageCode: model.languageCode,
          religion: model.religion,
          occupationId: model.occupationId,
          region: model.region,
          taxPayerNumber: model.taxPayerNumber,
          nationalHealthServiceNumber: model.nationalHealthServiceNumber,
          socialSecurityNumber: model.socialSecurityNumber,
        };

        // Edit user
        const updatedUser = await UsersRepository.instance.updateById(model.id, requestBody);

        // Show toast
        snackBarContext.setOfficeToastNotification({
          severity: 'success',
          message: t(translations.user_form.success.edit_caregiver_success_message),
        });

        // Update submit button state
        officeContext.setUserFormSubmitButtonState(SubmitButtonState.Success);

        return updatedUser;
      } else if (props.userFormType === UserFormType.EditCurrentUser) {
        // -----> Edit current user <-----

        // Check id
        if (!authState.session?.id) {
          // Error
          throw new Error('Empty id.');
        }

        // Profile picture to upload
        let processedProfilePicture: string | undefined;
        if (profilePictureFile) {
          processedProfilePicture = await processProfilePicture(
            authState.session.id,
            profilePictureFile
          );
        } else {
          processedProfilePicture = model.picture;
        }

        // Request body
        // Note: We are ignoring the email because we can't update it for now.
        // By setting it to undefined, the backend will ignore the field.
        const requestBody: PutUser = {
          product: model.product,
          type: model.type,
          isActive: model.isActive,
          picture: processedProfilePicture,
          name: model.name,
          displayName: model.displayName,
          gender: model.gender,
          birthday: model.birthday, // UTC
          phoneNumber: phoneNumber,
          address: model.address,
          languageCode: model.languageCode,
          religion: model.religion,
          occupationId: model.occupationId,
          region: model.region,
          taxPayerNumber: model.taxPayerNumber,
          nationalHealthServiceNumber: model.nationalHealthServiceNumber,
          socialSecurityNumber: model.socialSecurityNumber,
        };

        // Update current user
        const updatedUser: User = await UsersRepository.instance.updateById(
          authState.session.id,
          requestBody
        );

        // Previous language code
        const previousLanguage: BackendLanguageTag | undefined =
          authState.session?.currentUser.languageCode;

        // Check if the language needs to be updated
        let changeLanguage:
          | {
              language: BackendLanguageTag;
              type: 'chosen-language' | 'browser-language' | 'default-language';
            }
          | undefined;
        const backendLanguageFromAppLanguage: BackendLanguageTag | undefined =
          getBackendLanguageFromAppLanguage();
        const backendLanguageFromBrowserLanguage: BackendLanguageTag | undefined =
          getBackendLanguageFromBrowserLanguage();
        if (
          model.languageCode &&
          previousLanguage &&
          model.languageCode !== previousLanguage &&
          backendLanguageFromAppLanguage !== model.languageCode
        ) {
          // The language will update to the new one selected by the user
          changeLanguage = {
            language: model.languageCode,
            type: 'chosen-language',
          };
        } else if (
          model.languageCode &&
          !previousLanguage &&
          backendLanguageFromAppLanguage !== model.languageCode
        ) {
          // The language will update to the new one selected by the user
          changeLanguage = {
            language: model.languageCode,
            type: 'chosen-language',
          };
        } else if (!model.languageCode && previousLanguage) {
          // The language will update to the device language or the default language
          if (
            backendLanguageFromBrowserLanguage &&
            backendLanguageFromAppLanguage !== backendLanguageFromBrowserLanguage
          ) {
            // Use browser language
            changeLanguage = {
              language: backendLanguageFromBrowserLanguage,
              type: 'browser-language',
            };
          } else if (
            !backendLanguageFromBrowserLanguage &&
            backendLanguageFromAppLanguage !== BackendLanguageTag.ptPT
          ) {
            // Use default language
            changeLanguage = {
              language: BackendLanguageTag.ptPT,
              type: 'default-language',
            };
          }
        }

        // Note: The user form in office context will update in the profile.me.screen.tsx by updating
        // the session in the auth context below. This will then update the models in the current state.

        // Update current user in authContext
        if (changeLanguage) {
          // Refresh session to update the productConfiguration and the user
          try {
            refreshSessionData(
              authState.session?.selectedRelationship?.id,
              authState.session?.selectedAffiliation?.id
            );
          } catch (error) {
            // Log error but let's continue normally.
            // The data was saved on the backend so all is good.
            console.debug('🔴 Could not refresh session.', error);
          }

          // Update the app language
          await updateAppLanguage(changeLanguage.language);

          // Subscribe to push notifications again.
          // Otherwise, the push notifications will be sent in another language.
          // Note: We don't wait because otherwise the screen won't stop loading until the user does something.
          subscribeFirebasePushNotifications(false);
        } else {
          // Update session with current user
          updateSessionCurrentUser(updatedUser);
        }

        // Success message
        let successMessage: string = t(
          translations.user_form.success.edit_current_user_success_message
        );
        if (changeLanguage) {
          // Update the success message
          switch (changeLanguage.type) {
            case 'chosen-language':
              successMessage +=
                ' ' +
                t(
                  translations.user_form.success
                    .edit_current_user_app_language_to_chosen_language_success_message,
                  {language: t(translations.languages[changeLanguage.language])}
                );
              break;
            case 'browser-language':
              successMessage +=
                ' ' +
                t(
                  translations.user_form.success
                    .edit_current_user_app_language_to_browser_language_success_message,
                  {language: t(translations.languages[changeLanguage.language])}
                );
              break;
            case 'default-language':
              successMessage +=
                ' ' +
                t(
                  translations.user_form.success
                    .edit_current_user_app_language_to_default_language_success_message,
                  {language: t(translations.languages[changeLanguage.language])}
                );
              break;
          }
        }

        // Show toast
        snackBarContext.setOfficeToastNotification({
          severity: 'success',
          message: successMessage,
        });

        // Update submit button state
        officeContext.setUserFormSubmitButtonState(SubmitButtonState.Success);

        return updatedUser;
      } else {
        // Throw error
        throw new Error('Missing permissions.');
      }
    } catch (error: any) {
      // Log
      console.debug('🔴 Could not edit/create the user.', error);

      // Check if it's a "licenses-number-exceeded" error
      if (
        error &&
        error.response &&
        error.response.data &&
        Array.isArray(error.response.data.errors) &&
        error.response.data.errors[0] &&
        error.response.data.errors[0].code === 'licenses-number-exceeded'
      ) {
        snackBarContext.setOfficeToastNotification({
          severity: 'error',
          message: model.id
            ? t(translations.user_form.errors.activate_senior_licenses_number_exceeded_error)
            : t(translations.user_form.errors.create_active_senior_licenses_number_exceeded_error),
        });
      }

      // Update submit button state
      officeContext.setUserFormSubmitButtonState(SubmitButtonState.Error);
    }
  };

  const handleUpdateRolesIds = async () => {
    // Stop execution if the model doesn't exist or the user don't have the right permissions
    // It's not expected to fall back here
    if (!model || !originalModel || !props.canEditRole) {
      return;
    }

    try {
      // Update submit button state
      officeContext.setUserFormSubmitButtonState(SubmitButtonState.Loading);

      // Check ids and affiliation type
      if (!props.entityId || !originalModel.id) {
        // Error
        throw new Error('Empty ids.');
      }

      // Update the affiliation
      await EntitiesRepository.instance.updateAffiliation(props.entityId, originalModel.id, {
        rolesIds,
        processNumber: processNumber || null,
      });

      // If the user updated their own affiliation, we need to update
      // the session because some data may have changed.
      // If an error occurs, it's not a problem.
      try {
        await refreshSessionData(
          authState.session?.selectedRelationship?.id,
          authState.session?.selectedAffiliation?.id
        );
      } catch (error) {
        // Error
        console.debug('🔴 Could not refresh session.', error);
      }

      // Show toast
      snackBarContext.setOfficeToastNotification({
        severity: 'success',
        message: t(translations.user_form.success.edit_caregiver_roles_ids_success_message),
      });

      // Update submit button state
      officeContext.setUserFormSubmitButtonState(SubmitButtonState.Success);
    } catch (error) {
      // Log
      console.debug('🔴 Could not edit the affiliation type.', error);

      // Update submit button state
      officeContext.setUserFormSubmitButtonState(SubmitButtonState.Error);
    }
  };

  const handleUpdateQuickAccessCode = async (): Promise<AffiliationQuickAccess | undefined> => {
    // Check type of change

    if (originalQuickAccessCode && quickAccessCode === '') {
      // Quick Access Code existed and will be removed
      await EntitiesRepository.instance.removeQuickAccessCode(
        officeContext.state.userForm?.affiliation?.entityId ?? '',
        officeContext.state.userForm?.user.id ?? ''
      );
      setQuickAccessCode('');
      setOriginalQuickAccessCode('');
      return undefined;
    } else if (originalQuickAccessCode && quickAccessCode !== '****') {
      // Quick Access Code existed and is now different
      const updatedAffiliationQuickAccess: AffiliationQuickAccess =
        await EntitiesRepository.instance.updateQuickAccessCode(
          officeContext.state.userForm?.affiliation?.entityId ?? '',
          officeContext.state.userForm?.user.id ?? '',
          quickAccessCode
        );
      updatedAffiliationQuickAccess.code = '****';
      setQuickAccessCode('****');
      setOriginalQuickAccessCode('****');
      return updatedAffiliationQuickAccess;
    } else if (!originalQuickAccessCode && quickAccessCode && quickAccessCode.length > 0) {
      // Quick Access Code did not existed and it will now be created
      const createdAffiliationQuickAccess = await EntitiesRepository.instance.updateQuickAccessCode(
        officeContext.state.userForm?.affiliation?.entityId ?? '',
        officeContext.state.userForm?.user.id ?? '',
        quickAccessCode
      );
      createdAffiliationQuickAccess.code = '****';
      setQuickAccessCode('****');
      setOriginalQuickAccessCode('****');
      return createdAffiliationQuickAccess;
    } else if (originalQuickAccessCode && quickAccessCode === '****') {
      // Quick Access Code not changed
      return officeContext.state.userForm?.affiliationQuickAccess;
    }

    // Something not expected happened
    return undefined;
  };

  // ----- Handle change ------

  // Handle text fields
  const handleChangeTextField = (field: keyof User, value: boolean | string | undefined) => {
    // Process value
    let finalValue: string | boolean | undefined | null = value;

    if (value === undefined || value === '') {
      // Note: To actually empty data in DB we need to send null
      finalValue = null;
    }

    // Set it
    setModel(
      (u) =>
        ({
          ...u,
          [field]: finalValue,
        }) as User
    );
  };

  // Handle profile picture change
  const handleChangeProfilePicture = (image?: {base64: string; file: File}) => {
    if (image) {
      // Update profile picture and model
      setProfilePictureFile(image.file);
      handleChangeTextField('picture', image.base64);
    } else {
      // Clear
      setProfilePictureFile(undefined);
      handleChangeTextField('picture', undefined);
    }
  };

  // Strings to use in the form
  let routerDialogTitle: string = '';
  let routerDialogContent: string = '';
  let routerCancelToast: string = '';
  switch (props.userFormType) {
    case UserFormType.EditCurrentUser:
      routerDialogTitle = t(translations.user_form.current_user_router_prompt_edit_title);
      routerDialogContent = t(translations.user_form.current_user_router_prompt_edit_message);
      routerCancelToast = t(translations.user_form.current_user_edit_cancel_toast);
      break;
    case UserFormType.EditCaregiver_SystemAdministrator:
    case UserFormType.CreateEditRemoveSenior_ProfessionalCaregiver:
      routerDialogTitle = t(translations.user_form.caregiver_edit_router_prompt_edit_title, {
        name: originalModel?.name || '',
      });
      routerDialogContent = t(translations.user_form.caregiver_edit_router_prompt_edit_message);
      routerCancelToast = t(translations.user_form.caregiver_edit_cancel_toast, {
        name: originalModel?.name || '',
      });
      break;
    case UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverAdministrator:
      routerDialogTitle = t(
        translations.user_form.caregiver_edit_affiliation_type_router_prompt_edit_title,
        {
          name: originalModel?.name || '',
        }
      );
      routerDialogContent = t(
        translations.user_form.caregiver_edit_affiliation_type_router_prompt_edit_message
      );
      routerCancelToast = t(translations.user_form.caregiver_edit_affiliation_type_cancel_toast, {
        name: originalModel?.name || '',
      });
      break;
    default:
      break;
  }

  return (
    <ContentPage>
      {/* Form */}
      {model && !officeContext.state.userFormLoadingState ? (
        <form className="user-form-container">
          {(props.userFormType ===
            UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
            props.userFormType ===
              UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverAdministrator) && (
            <Box className="user-form-view-only-avatar-container">
              <Avatar
                alt={model.name}
                src={model.picture}
                sx={{
                  width: `${avatarSizesAndCoefficients.avatarSizes.extraGiant}px`,
                  height: `${avatarSizesAndCoefficients.avatarSizes.extraGiant}px`,
                }}
              >
                <Person
                  sx={{
                    fontSize:
                      avatarSizesAndCoefficients.avatarSizes.extraGiant *
                      avatarSizesAndCoefficients.iconSizeCoefficient,
                  }}
                />
              </Avatar>
            </Box>
          )}
          {(props.userFormType === UserFormType.EditCurrentUser ||
            props.userFormType === UserFormType.EditCaregiver_SystemAdministrator ||
            props.userFormType === UserFormType.CreateEditRemoveSenior_ProfessionalCaregiver) && (
            <EditPicture
              className="user-form-edit-profile-picture-container"
              type="user"
              alt={t(translations.user_form.profile_picture_input)}
              src={model.picture}
              handleChange={handleChangeProfilePicture}
              disabled={
                officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
                officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
              }
              aspect={appSizes.profileImageSize.width / appSizes.profileImageSize.height}
              size={`${appSizes.profileImageSize.width}x${appSizes.profileImageSize.height}`}
              alertMessage={t(translations.user_form.profile_picture_info_message)}
              useFileCropper
              clearable
            />
          )}
          <Box className="user-form-line-container">
            <FlatInput
              className="user-form-line-2"
              id="user-form-full-name-input"
              label={t(translations.user_form.full_name_input)}
              placeholder={t(translations.user_form.input_placeholder, {
                type: String(t(translations.user_form.full_name_input)).toLowerCase(),
              })}
              value={model.name}
              onChange={(e) => handleChangeTextField('name', e.currentTarget.value)}
              onBlur={() => setShowFullNameError(true)}
              disabled={
                props.userFormType ===
                  UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
                props.userFormType ===
                  UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverAdministrator ||
                officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
                officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
              }
              disabledTooltip={t(translations.user_form.input_read_only_tooltip)}
              errors={fullNameError}
              counter={
                fullNameError
                  ? {
                      current: model.name?.length ?? 0,
                      max: FULL_NAME_MAX_CHARACTERS,
                    }
                  : undefined
              }
              required
              type="text"
            />
          </Box>
          <Box className="user-form-line-container">
            <Stack className="user-form-line-3-left" flexDirection="column">
              <FlatInput
                id="user-form-display-name-input"
                type="text"
                label={t(translations.user_form.display_name_input)}
                placeholder={t(translations.user_form.input_placeholder, {
                  type: String(t(translations.user_form.display_name_input)).toLowerCase(),
                })}
                value={model.displayName || ''}
                onChange={(e) => handleChangeTextField('displayName', e.currentTarget.value)}
                onBlur={() => setShowDisplayNameError(true)}
                disabled={
                  props.userFormType ===
                    UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
                  props.userFormType ===
                    UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverAdministrator ||
                  officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
                  officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
                }
                disabledTooltip={t(translations.user_form.input_read_only_tooltip)}
                counter={
                  displayNameError
                    ? {
                        current: model.displayName?.length ?? 0,
                        max: DISPLAY_NAME_MAX_CHARACTERS,
                      }
                    : undefined
                }
                required
                errors={displayNameError}
              />
              {props.userFormType !==
                UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator &&
                props.userFormType !==
                  UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverAdministrator && (
                  <Alert
                    severity="info"
                    icon={<BsInfoCircle fontSize="16px" />}
                    sx={{
                      marginTop: displayNameError ? '25px' : '5px',
                    }}
                  >
                    {props.userFormType === UserFormType.EditCurrentUser
                      ? t(translations.user_form.display_name_current_user_info_message)
                      : t(translations.user_form.display_name_other_user_info_message)}
                  </Alert>
                )}
            </Stack>
            <Box className="user-form-line-3-right">
              <FlatSelectDropdown
                id="user-form-state-select"
                label={t(translations.user_form.state_input, {
                  role: t(translations.user_types.caregiver),
                })}
                value={
                  model.isActive !== null
                    ? {
                        key: String(model.isActive),
                        label: model.isActive
                          ? t(translations.user_form.state_value_true)
                          : t(translations.user_form.state_value_false),
                      }
                    : undefined
                }
                options={[
                  {key: 'true', label: t(translations.user_form.state_value_true)},
                  {key: 'false', label: t(translations.user_form.state_value_false)},
                ]}
                onChange={(option) => {
                  handleChangeTextField('isActive', JSON.parse(option.key));
                }}
                required={props.userFormType !== UserFormType.EditCurrentUser}
                disabled={
                  props.userFormType ===
                    UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
                  props.userFormType ===
                    UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverAdministrator ||
                  props.userFormType === UserFormType.EditCurrentUser ||
                  officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
                  officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
                }
                disabledTooltip={t(translations.user_form.input_read_only_tooltip)}
              />
            </Box>
          </Box>
          <Box className="user-form-line-container">
            <Box className="user-form-line-left">
              <FlatSelectDate
                label={t(translations.user_form.birthday_input)}
                value={model.birthday ? new Date(model.birthday) : undefined}
                disabled={
                  props.userFormType ===
                    UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
                  props.userFormType ===
                    UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverAdministrator ||
                  officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
                  officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
                }
                placeholder={t(translations.user_form.date_placeholder_input)}
                allowClear
                disableFuture
                onChange={(date) => {
                  const dateISO = date
                    ? moment(date)
                        .utc()
                        .set({hours: 0, minutes: 0, seconds: 0, milliseconds: 0})
                        .toISOString()
                    : undefined;

                  handleChangeTextField('birthday', dateISO);
                }}
              />
            </Box>
            <Box className="user-form-line-right">
              <FlatSelectDropdown
                id="user-form-gender-select"
                label={t(translations.user_form.gender_input)}
                placeholder={t(translations.user_form.gender_placeholder_input)}
                value={
                  model.gender
                    ? {
                        key: model.gender,
                        label: t(translations.genders[model.gender as UserGender]),
                      }
                    : undefined
                }
                options={genders.map((gender) => {
                  return {key: gender.type, label: t(gender.translation)};
                })}
                onChange={(e) => {
                  handleChangeTextField('gender', e.key);
                }}
                disabled={
                  props.userFormType ===
                    UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
                  props.userFormType ===
                    UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverAdministrator ||
                  officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
                  officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
                }
                disabledTooltip={t(translations.user_form.input_read_only_tooltip)}
              />
            </Box>
          </Box>

          <Box className="user-form-line-container">
            <Box className="user-form-line-left">
              <FlatInput
                id="user-form-address-input"
                type="text"
                label={t(translations.user_form.address_input)}
                placeholder={t(translations.user_form.address_placeholder_input)}
                value={model.address || ''}
                onChange={(e) => handleChangeTextField('address', e.currentTarget.value)}
                disabled={
                  props.userFormType ===
                    UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
                  props.userFormType ===
                    UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverAdministrator ||
                  officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
                  officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
                }
                disabledTooltip={t(translations.user_form.input_read_only_tooltip)}
                multiline
                rows={3}
              />
            </Box>
            <Box className="user-form-line-right">
              <PhoneNumberInput
                id="user-form-phone-number-input"
                label={t(translations.user_form.mobile_phone_number_input)}
                countryCode={countryCode}
                phoneNumber={model.phoneNumber || ''}
                handleChangeCountryCode={(value) => {
                  setCountryCode(value);
                }}
                handleChangePhoneNumber={(value) => handleChangeTextField('phoneNumber', value)}
                disabled={
                  props.userFormType ===
                    UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
                  props.userFormType ===
                    UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverAdministrator ||
                  officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
                  officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
                }
                placeholder={t(translations.user_form.mobile_phone_number_placeholder_input)}
                handleBlur={() => setShowPhoneNumberError(true)}
                error={phoneNumberError}
                required={false}
              />
            </Box>
          </Box>
          <Box className="user-form-line-container">
            <Box className="user-form-line-left">
              <FlatSelectDropdown
                id="user-form-language-select"
                label={t(translations.user_form.language_input)}
                placeholder={t(translations.user_form.language_placeholder_input)}
                clearable
                value={
                  model.languageCode
                    ? {
                        key: model.languageCode,
                        label: t(translations.languages[model.languageCode as BackendLanguageTag]),
                      }
                    : undefined
                }
                options={languages.map((language) => {
                  return {key: language.type, label: t(language.translation)};
                })}
                onChange={(e) => {
                  handleChangeTextField('languageCode', e.key);
                }}
                disabled={
                  props.userFormType ===
                    UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
                  props.userFormType ===
                    UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverAdministrator ||
                  officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
                  officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
                }
                disabledTooltip={t(translations.user_form.input_read_only_tooltip)}
              />
            </Box>
            <Box className="user-form-line-right">
              <FlatSelectDropdown
                id="user-form-region-select"
                label={t(translations.user_form.region_input)}
                placeholder={t(translations.user_form.region_placeholder_input)}
                clearable
                value={
                  model.region
                    ? {
                        key: model.region,
                        label: t(translations.regions[model.region as RegionCode]),
                      }
                    : undefined
                }
                options={regions.map((region) => {
                  return {key: region.type, label: t(region.translation)};
                })}
                onChange={(e) => {
                  handleChangeTextField('region', e.key);
                }}
                disabled={
                  props.userFormType ===
                    UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
                  props.userFormType ===
                    UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverAdministrator ||
                  officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
                  officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
                }
                disabledTooltip={t(translations.user_form.input_read_only_tooltip)}
              />
            </Box>
          </Box>
          <Box className="user-form-line-container">
            <Box className="user-form-line-left">
              <FlatInput
                id="user-form-tax-payer-number-input"
                type="text"
                label={t(translations.user_form.tax_payer_number_input)}
                placeholder={t(translations.user_form.tax_payer_number_input_placeholder)}
                value={model.taxPayerNumber || ''}
                onChange={(e) => handleChangeTextField('taxPayerNumber', e.currentTarget.value)}
                onBlur={() => setShowTaxPayerNumberError(true)}
                errors={taxPayerNumberError}
                disabledTooltip={t(translations.user_form.input_read_only_tooltip)}
                disabled={
                  props.userFormType ===
                    UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
                  props.userFormType ===
                    UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverAdministrator ||
                  officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
                  officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
                }
              />
            </Box>
            <Box className="user-form-line-right">
              <FlatInput
                className="user-form-line-4"
                id="user-form-email-input"
                type="email"
                label={t(translations.user_form.email_input)}
                placeholder={t(translations.user_form.email_placeholder_input)}
                value={model.email || ''}
                onChange={(e) => handleChangeTextField('email', e.currentTarget.value)}
                onBlur={() => setShowEmailError(true)}
                errors={emailError}
                disabled
                disabledTooltip={
                  props.userFormType ===
                    UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
                  props.userFormType ===
                    UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverAdministrator
                    ? t(translations.user_form.input_read_only_tooltip)
                    : props.userFormType === UserFormType.EditCurrentUser
                      ? t(translations.user_form.current_user_edit_email_not_allowed_tooltip_title)
                      : props.userFormType === UserFormType.EditCaregiver_SystemAdministrator ||
                          props.userFormType ===
                            UserFormType.CreateEditRemoveSenior_ProfessionalCaregiver
                        ? t(translations.user_form.caregiver_edit_email_not_allowed_tooltip_title)
                        : undefined
                }
              />
            </Box>
          </Box>
          <Box className="user-form-line-container">
            <OccupationInput
              className="user-form-line-10"
              id="user-form-occupation-select"
              label={t(translations.user_form.profession_input)}
              value={occupation}
              handleChange={(value?: OccupationOption) => {
                setOccupation(value);
                handleChangeTextField('occupationId', value?.occupationId);
              }}
              required={false}
              disabled={
                props.userFormType ===
                  UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
                props.userFormType ===
                  UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverAdministrator ||
                officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
                officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
              }
              disabledTooltip={t(translations.user_form.input_read_only_tooltip)}
              userType={UserType.Caregiver}
            />
          </Box>

          {/* PROCESS NUMBER */}
          {model && model.id && props.entityId && (
            <Box className="user-form-line-container">
              <FlatInput
                className="user-form-line-2"
                id="user-form-process-number-input"
                type="text"
                label={t(translations.user_form.process_number_input)}
                placeholder={t(translations.user_form.process_number_input_placeholder)}
                value={processNumber || ''}
                onChange={(e) => {
                  setProcessNumber(e.currentTarget.value || undefined);
                }}
                onBlur={() => setShowProcessNumberError(true)}
                disabled={
                  props.userFormType ===
                    UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
                  props.userFormType ===
                    UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverAdministrator ||
                  officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
                  officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
                }
                disabledTooltip={t(translations.user_form.input_read_only_tooltip)}
                errors={processNumberError}
                counter={
                  processNumber && processNumberError
                    ? {
                        current: processNumber.length,
                        max: PROCESS_NUMBER_MAX_CHARACTERS,
                      }
                    : undefined
                }
              />
            </Box>
          )}

          {/* ROLES INPUT */}
          {model &&
            model.id &&
            // Roles are associated with user affliations, not with user data itself. If props.entityId is undefined don't render.
            props.entityId &&
            rolesIds &&
            rolesOptions && (
              <Box className="user-form-line-container user-form-affiliation-type-field">
                <FlatMultiTextSelect
                  label={t(translations.user_form.roles_ids_input)}
                  placeholder={t(translations.user_form.roles_ids_placeholder_input)}
                  value={rolesOptions?.filter((option) => rolesIds?.includes(option.key)) ?? []}
                  options={rolesOptions ?? []}
                  onChange={(selected) => {
                    setRolesIds(
                      Array.isArray(selected) ? selected.map((option) => option.key) : undefined
                    );
                  }}
                  disabled={
                    !props.canEditRole ||
                    officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
                    officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
                  }
                  disabledTooltip={t(translations.user_form.input_read_only_tooltip)}
                />
              </Box>
            )}

          {/* QUICK ACCESS CODE */}
          {canShowQuickAccessCode && (
            <Box
              className="user-form-line-container user-form-quick-access-code-field"
              sx={{
                display: 'flex',
                alignItems: 'flex-end',
              }}
            >
              <FlatInput
                className="user-form-quick-access-code"
                id="user-form-quick-access-code-input"
                label={t(translations.user_form.quick_access_code_input)}
                placeholder={t(translations.user_form.input_placeholder, {
                  type: String(t(translations.user_form.quick_access_code_input)).toLowerCase(),
                })}
                value={quickAccessCode}
                onChange={(e) => setQuickAccessCode(e.currentTarget.value)}
                disabled={
                  officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
                  officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading ||
                  quickAccessCode === '****'
                }
                errors={quickAccessCodeError}
                counter={
                  quickAccessCodeError
                    ? {
                        current: quickAccessCode?.length ?? 0,
                        max: QUICK_ACCESS_CODE_MAX_CHARACTERS,
                      }
                    : undefined
                }
                type="password"
                autoFocus={false}
                autoComplete="one-time-code"
                onBlur={() => setShowQuickAccessCodeError(true)}
                disabledTooltip={t(translations.user_form.quick_access_code_disabled)}
              />
              {quickAccessCode === '****' && props.canEditRole && (
                <Button
                  variant="outlined"
                  sx={{
                    marginLeft: '12px',
                  }}
                  disabled={
                    officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
                    officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
                  }
                  onClick={() => setQuickAccessCode('')}
                >
                  {t(translations.user_form.quick_access_code_redefine_button)}
                </Button>
              )}
            </Box>
          )}

          <Stack
            className="user-form-footer-container"
            flexDirection="row"
            gap="10px"
            justifyContent="space-between"
          >
            <Button
              variant="text"
              color="error"
              sx={{
                width: '100%',
                height: '40px',
                padding: '8px 16px',
                borderRadius: '5px',
                maxWidth: '252px',
              }}
              onClick={() => history.push(props.redirectUrl)}
            >
              {t(translations.user_form.cancel_button)}
            </Button>

            <SubmitButton
              disabled={submitButtonDisabled}
              state={officeContext.state.userFormSubmitButtonState || SubmitButtonState.Normal}
              onStateChange={(newState: SubmitButtonState) => {
                officeContext.setUserFormSubmitButtonState(newState);
              }}
              normalStateLabel={t(translations.user_form.save_button)}
              loadingStateLabel={t(translations.user_form.saving_button)}
              successStateLabel={t(translations.user_form.success.save_success_button)}
              errorStateLabel={t(translations.user_form.errors.error_on_save_button)}
              onClick={handleSubmit}
              sx={{
                width: '100%',
                height: '40px',
                padding: '8px 16px',
                borderRadius: '5px',
                maxWidth: '252px',
              }}
            />
          </Stack>
        </form>
      ) : (
        <Stack
          height={`calc(100vh - 108px - (2 * 20px) - 70px - 40px)`}
          sx={{justifyContent: 'center', alignItems: 'center'}}
        >
          <LoadingContent />
        </Stack>
      )}

      {/* Router */}
      {model &&
        (props.userFormType === UserFormType.EditCurrentUser ||
          props.userFormType === UserFormType.EditCaregiver_SystemAdministrator ||
          props.userFormType === UserFormType.CreateEditRemoveSenior_ProfessionalCaregiver ||
          props.userFormType ===
            UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverAdministrator) && (
          <RouterPrompt
            // Show prompt if the data changed but make sure the path
            // is not changing because the user was deleted/created
            when={
              dataChanged &&
              officeContext.state.userFormDeleteButtonState !== SubmitButtonState.Loading &&
              officeContext.state.userFormSubmitButtonState !== SubmitButtonState.Loading
            }
            subtitle={routerDialogTitle}
            message={routerDialogContent}
            onOK={() => true}
            onCancel={() => false}
            toastNotification={{
              severity: 'info',
              message: routerCancelToast,
            }}
          />
        )}
    </ContentPage>
  );
};
